import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Product } from '../../products/products.model';
import { ProductsService } from '../../products/products.service';
import { LocationDto } from '../inventory.model';
import { InventoryService } from '../inventory.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../auth/auth.service';


@Component({
  selector: 'app-inventory-list',
  templateUrl: './inventory-list.component.html',
  styleUrls: ['./inventory-list.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
})
export class InventoryListComponent implements OnInit {
  private inventoryService = inject(InventoryService);
  private productService = inject(ProductsService);
  private toastr = inject(ToastrService);
  private authService = inject(AuthService);

  inventoryList: any[] = [];
  /** Roots (warehouses/stores) as the API returns them, children nested. */
  locations: LocationDto[] = [];
  /**
   * What the dropdown actually renders: every root followed by its sub-areas
   * (damage area, …), indented. A sub-area is offered whenever its parent is,
   * because access is granted on the parent only.
   */
  locationOptions: LocationOption[] = [];
  locationName: string = '';
  /** Strip above the table: sellable / in transit / per sub-area totals. */
  summary: LocationSummary | null = null;
  products: Product[] = [];

  searchName = '';
  searchSKU = '';
  selectedLocationId: any = '';

  currentPage = 1;
  pageSize = 10;
  pageCount = 1;
  totalItems = 0;
  loading = false;
  exporting = false;

  ngOnInit(): void {
    this.loadProductsAndLocations();
  }

  loadProductsAndLocations() {
    // Preload products
    this.productService
      .getProducts({ page: 1, limit: 1000 }, '', false)
      .subscribe((res) => {
        this.products = res.data;
      });

    // Preload locations
    this.inventoryService.getAllLocations().subscribe((res) => {
      // Non-admin users only see the stores/warehouses they were granted
      // access to (UserStoreAccess); admins keep the full tenant list.
      const accessibleIds = new Set(
        this.authService.getStoreAccess().map((a) => a.locationId)
      );
      this.locations =
        this.authService.isAdmin() || accessibleIds.size === 0
          ? res
          : res.filter((l: any) => accessibleIds.has(l.id));
      this.locationOptions = this.flattenForPicker(this.locations);

      // Default to the store/warehouse chosen at login (header switcher).
      const activeLocationId = this.authService.getActiveLocationId();
      if (activeLocationId) {
        this.selectedLocationId = activeLocationId;
        this.getInventoriesByLocation();
        this.loadSummary();
      }
    });
  }

  loadInventory(filters: { productIds?: string[] } = {}) {
    this.loading = true;
    const params: any = {
      page: this.currentPage,
      limit: this.pageSize,
      context: 'INVENTORY_LIST',
    };
    if (filters.productIds) {
      params.productIds = filters.productIds;
    }
    if (this.selectedLocationId) {
      params.locationId = this.selectedLocationId;
    }

    this.inventoryService.getInventoryByQueryAndLocation(params).subscribe({
      next: (res: any) => {
        this.inventoryList = res.data || [];
        this.totalItems = res.total || 0;
        this.pageCount =
          res.pageCount || Math.ceil(this.totalItems / this.pageSize);
        this.loading = false;
      },
      error: (err) => {
        this.toastr.error(err.error?.message || 'Failed to load inventory');
        this.inventoryList = [];
        this.pageCount = 1;
        this.loading = false;
      },
    });
  }

  /**
   * Roots first, each followed by its active sub-areas. A damage area under
   * WH-RR reads "↳ Damage Area" straight beneath "WH-RR – RRWH01".
   */
  private flattenForPicker(roots: LocationDto[]): LocationOption[] {
    const out: LocationOption[] = [];
    for (const root of roots) {
      out.push({ id: root.id, name: root.name, type: root.type, depth: 0, parentId: null });
      for (const child of root.childLocations ?? []) {
        if (child.isActive === false) continue;
        out.push({
          id: child.id,
          name: child.name,
          type: child.type,
          depth: 1,
          parentId: root.id,
        });
      }
    }
    return out;
  }

  /** The strip is shown on a root (warehouse/store), never on a sub-area. */
  loadSummary(): void {
    this.summary = null;
    const option = this.locationOptions.find((o) => o.id === this.selectedLocationId);
    if (!option || option.depth > 0) return;
    this.inventoryService.getLocationStockSummary(option.id).subscribe({
      next: (res: any) => (this.summary = res ?? null),
      error: () => (this.summary = null),
    });
  }

  /** Goods reach a warehouse by GRN and leave by TO; a store is the other side. */
  get summaryIsWarehouse(): boolean {
    return String(this.summary?.location?.type ?? '').toLowerCase() === 'warehouse';
  }

  /** Jump the dropdown to a sub-area from the strip. */
  selectLocation(id: string): void {
    this.selectedLocationId = id;
    this.onSearch();
  }

  onSearch() {
    this.currentPage = 1;
    this.loadSummary();
    const nameQuery = this.searchName.trim();
    const skuQuery = this.searchSKU.trim();

    // If no query OR no location selected, load full inventory
    if ((!nameQuery && !skuQuery) || !this.selectedLocationId) {
      this.loadInventory();
      return;
    }

    // Build params object like other calls
    const params: any = {
      query: skuQuery || nameQuery, // put query FIRST
      locationId: this.selectedLocationId,
      page: this.currentPage,
      limit: this.pageSize,
      context: 'INVENTORY_LIST_SEARCH',
    };

    this.inventoryService.getInventoryByQueryAndLocation(params).subscribe({
      next: (res: any) => {
        this.inventoryList = res.data || res;
        this.totalItems = res.total || this.inventoryList.length;
        this.pageCount = Math.ceil(this.totalItems / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err.error?.message || 'Failed to search inventory');
        this.inventoryList = [];
        this.pageCount = 1;
      },
    });
  }

  getInventoriesByLocation() {
    this.currentPage = 1;

    if (!this.selectedLocationId) {
      // If no location is selected, load all inventories
      this.loadInventory();
      return;
    }

    this.loading = true;
    const params = {
      locationId: this.selectedLocationId,
      query: this.searchSKU.trim() || this.searchName.trim(),
      page: this.currentPage,
      limit: this.pageSize,
    };

    this.inventoryService.getInventoryByQueryAndLocation(params).subscribe({
      next: (res: any) => {
        this.inventoryList = res.data || res; // adapt to API response
        this.totalItems = res.total || this.inventoryList.length;
        this.pageCount = Math.ceil(this.totalItems / this.pageSize);
        this.loading = false;
      },
      error: (err) => {
        this.toastr.error(
          err.error?.message || 'Failed to load inventory by location'
        );
        this.inventoryList = [];
        this.pageCount = 1;
        this.loading = false;
      },
    });
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.pageCount) {
      this.currentPage = page;
      this.loadInventory();
    }
  }

  pageSizeChange() {
    this.currentPage = 1;
    this.loadInventory();
  }

  // Pagination array (max 5 pages visible)
  get visiblePages(): number[] {
    const total = this.pageCount;
    const current = this.currentPage;
    const maxVisible = 5;
    let start = Math.max(current - Math.floor(maxVisible / 2), 1);
    let end = start + maxVisible - 1;
    if (end > total) {
      end = total;
      start = Math.max(end - maxVisible + 1, 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  // Utility methods
  // Show the long description in the Name column; fall back to the short name
  // when a product has no long description.
  getProductName(item: any): string {
    return (
      item.variant?.product?.longDescription ||
      item.longDescription ||
      item.variant?.product?.name ||
      item.name
    );
  }
  getProductSKU(item: any): string {
    return item.variant?.product?.sku || item.sku;
  }
  getVariantName(item: any): string {
    if (item?.variant?.variantName) {
      return item.variant.variantName;
    }
    if (item?.variants?.length) {
      return item.variants[0]?.variantName || '-';
    }
    return '-';
  }
  getLocationName(item: any): string {
    return item.location?.name || item.locationName;
  }

  exportToExcel(): void {
    if (!this.selectedLocationId) {
      this.toastr.warning('Select a location to export stock status');
      return;
    }
    this.exporting = true;
    const payload: { locationId: string; query?: string } = {
      locationId: this.selectedLocationId,
    };
    const query = this.searchSKU.trim() || this.searchName.trim();
    if (query) {
      payload.query = query;
    }

    this.inventoryService.exportStockStatus(payload).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `stock-status-${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.exporting = false;
        this.toastr.success('Stock status exported successfully!');
      },
      error: () => {
        this.exporting = false;
        this.toastr.error('Failed to export stock status.');
      },
    });
  }

  deleteItem(id: string) {
    this.inventoryService.deleteInventoryById(id).subscribe(() => {
      this.toastr.success('Inventory deleted');
      this.loadInventory();
    });
  }
  formatExpiryDate(dateStr?: string): string {
    if (!dateStr) return '-';

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';

    // Returns YYYY-MM-DD
    return date.toISOString().split('T')[0];
  }
}

interface LocationOption {
  id: string;
  name: string;
  type: string;
  /** 0 = warehouse/store, 1 = sub-area under it. */
  depth: number;
  parentId: string | null;
}

interface LocationSummary {
  location: { id: string; name: string; type: string };
  onHandQty: number;
  /** Warehouse view: dispatched to stores, not yet received there. */
  inTransitOutboundQty: number;
  inTransitOutboundDocs: number;
  /** Store view: sent here by the warehouse, not yet received. */
  inTransitInboundQty: number;
  subAreas: { id: string; name: string; type: string; qty: number; skuCount: number }[];
}
